import { parseOptions } from './utils';

// Toggle readonly/disabled for a target input/select/textarea when a button with `data-lock` is clicked.
// - If `data-lock` contains JSON with a `targetSelector` field it will use that selector.
// - Otherwise it will look for the immediate parent of the button and try to find input/select/textarea there.
// - For <select> elements, when SlimSelect is present we do NOT disable the underlying <select> (disabled selects don't post).
//   Instead we toggle a readonly class on the SlimSelect container and the select so the control is visually/read-only
//   while keeping the original <select> enabled so its value is posted on submit.
// - For non-SlimSelect selects we fall back to toggling `disabled` (rare).
// - Updates the two icon <i> elements inside the button by toggling the `hidden` class.

type DataLockOptions = { targetSelector?: string };

function findClosestTarget(button: HTMLElement, opts: DataLockOptions): HTMLElement | null {
  if (opts?.targetSelector) {
    try {
      const el = document.querySelector(opts.targetSelector);
      if (el instanceof HTMLElement) return el;
    } catch {
      // invalid selector - fall back to parent-based lookup
    }
  }

  const parent = button.parentElement;
  if (parent) {
    if (parent.matches && parent.matches("input, textarea, select")) {
      return parent as HTMLElement;
    }

    const found = parent.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("input, textarea, select");
    if (found) return found as HTMLElement;
  }

  const siblingSelectors = "input, textarea, select";
  let el = button.previousElementSibling as Element | null;
  while (el) {
    if ((el as Element).matches && (el as Element).matches(siblingSelectors)) return el as HTMLElement;
    el = el.previousElementSibling;
  }
  el = button.nextElementSibling as Element | null;
  while (el) {
    if ((el as Element).matches && (el as Element).matches(siblingSelectors)) return el as HTMLElement;
    el = el.nextElementSibling;
  }

  return null;
}

/**
 * Try to find the SlimSelect rendered container for a given <select>.
 * This uses a few heuristics: common SlimSelect container classes or siblings that contain "ss" / "slim".
 */
function findSlimSelectContainer(sel: HTMLSelectElement): HTMLElement | null {
  // 1) common immediate sibling (SlimSelect often inserts its UI right after the <select>)
  const next = sel.nextElementSibling as HTMLElement | null;
  if (next && /(^|\s)(ss-|ss_|slim|slimselect|ss-wrapper|ss-main)/i.test(next.className)) return next;

  // 2) try previous sibling
  const prev = sel.previousElementSibling as HTMLElement | null;
  if (prev && /(^|\s)(ss-|ss_|slim|slimselect|ss-wrapper|ss-main)/i.test(prev.className)) return prev;

  // 3) search nearby siblings up to 3 elements
  let sib = sel.nextElementSibling as HTMLElement | null;
  for (let i = 0; i < 3 && sib; i++, sib = sib.nextElementSibling as HTMLElement | null) {
    if (/ss|slim|slimselect/i.test(sib.className)) return sib;
  }

  // 4) look inside parent for an element that looks like a SlimSelect UI
  const parent = sel.parentElement;
  if (parent) {
    const found = parent.querySelector<HTMLElement>("[data-slimselect]");
    if (found) return found;
  }

  return null;
}

function toggleReadonlyForElement(el: HTMLElement): void {
  const tag = el.tagName.toLowerCase();

  switch (tag) {
    case "select": {
      const sel = el as HTMLSelectElement;

      // If SlimSelect is used, prefer to make the SlimSelect UI read-only instead of disabling the underlying <select>
      const slimContainer = findSlimSelectContainer(sel);

      if (slimContainer) {
        // Toggle a class on both container and select so styling can reflect readonly state.
        const isNowReadOnly = !sel.classList.contains("readonly");
        if (isNowReadOnly) {
          sel.classList.add("readonly");
          sel.setAttribute("aria-readonly", "true");
          slimContainer.classList.add("readonly");
          // prevent pointer events on the rendered UI so user can't interact
          slimContainer.style.pointerEvents = "none";
        } else {
          sel.classList.remove("readonly");
          sel.removeAttribute("aria-readonly");
          slimContainer.classList.remove("readonly");
          slimContainer.style.pointerEvents = "";
        }

        // Keep the underlying select enabled so it posts its value on submit.
        sel.disabled = false;
      } else {
        // Fallback: no SlimSelect UI found � toggle disabled and a readonly class.

        if (sel.disabled) {
          sel.classList.add("readonly");
          sel.setAttribute("aria-disabled", "true");
        } else {
          sel.classList.remove("readonly");
          sel.removeAttribute("aria-disabled");
        }
      }

      break;
    }

    case "input":
    case "textarea": {
      const inp = el as HTMLInputElement | HTMLTextAreaElement;
      inp.readOnly = !inp.readOnly;
      if (inp.readOnly) inp.classList.add("readonly");
      else inp.classList.remove("readonly");
      if (!inp.readOnly) {
        setTimeout(() => inp.focus(), 0);
      }
      break;
    }

    default: {
      const isReadOnly = el.getAttribute("aria-readonly") === "true";
      el.setAttribute("aria-readonly", (!isReadOnly).toString());
    }
  }
}

function toggleButtonIcons(button: HTMLElement): void {
  const icons = Array.from(button.querySelectorAll<HTMLElement>("i"));
  if (icons.length >= 2) {
    icons.forEach(i => i.classList.toggle("hidden"));
  } else {
    const pressed = button.getAttribute("aria-pressed") === "true";
    button.setAttribute("aria-pressed", (!pressed).toString());
  }
}

function initDataLock(): void {
  document.querySelectorAll<HTMLElement>("[data-lock]").forEach(buttonEl => {
    const button = buttonEl as HTMLElement;
    if ((button as any).__dataLockInitialized) return;
    (button as any).__dataLockInitialized = true;

    button.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();

      const raw = button.getAttribute("data-lock");
      const opts = parseOptions<DataLockOptions>(raw, {});

      const target = findClosestTarget(button, opts);
      if (!target) {
        console.warn("data-lock: no target element found for", button, raw);
        return;
      }

      toggleReadonlyForElement(target);
      toggleButtonIcons(button);

      const isLocked = (target.tagName.toLowerCase() === "select")
        ? false === (target as HTMLSelectElement).disabled && (target as HTMLSelectElement).classList.contains("readonly")
        : !!((target as HTMLInputElement | HTMLTextAreaElement).readOnly);

      button.setAttribute("aria-pressed", isLocked.toString());
      button.setAttribute("title", isLocked ? "Unlock" : "Lock");
    });
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initDataLock);
} else {
  initDataLock();
}

export { initDataLock };
